import { useMemo } from 'react';
import { useAppSelector } from '@store/store.tsx';
import { selectIsStaff } from '@store/slices/userSlice.tsx';
import {
  selectCmsEnabled,
  selectSiteInfoStatus,
} from '@store/slices/settingsSlice.tsx';

export type CmsAccessStatus =
  | 'loading'
  | 'allowed'
  | 'forbidden_role'
  | 'forbidden_plan'
  | 'error';

export type CmsAccessReason = 'role' | 'plan';

export interface CmsAccessState {
  canAccess: boolean;
  status: CmsAccessStatus;
  reason: CmsAccessReason | null;
  isStaff: boolean;
  cmsEnabled: boolean;
  infoStatus: ReturnType<typeof selectSiteInfoStatus>;
  hasCapabilityData: boolean;
}

export const useCmsAccess = (): CmsAccessState => {
  const isStaff = useAppSelector(selectIsStaff);
  const infoStatus = useAppSelector(selectSiteInfoStatus);
  const cmsEnabled = useAppSelector((state) =>
    selectCmsEnabled({ settings: state.settings }),
  );
  const planCapabilities = useAppSelector(
    (state) => state.settings.planCapabilities,
  );
  const apps = useAppSelector((state) => state.settings.apps);

  const hasCapabilityData = useMemo(() => {
    const capabilityGroups = Object.values(planCapabilities ?? {});
    const hasCapabilities = capabilityGroups.some(
      (group) => group && Object.keys(group).length > 0,
    );
    return apps.length > 0 || hasCapabilities;
  }, [apps, planCapabilities]);

  const status: CmsAccessStatus = useMemo(() => {
    if (!isStaff) {
      return 'forbidden_role';
    }

    if (cmsEnabled) {
      return 'allowed';
    }

    if (!hasCapabilityData && (infoStatus === 'loading' || infoStatus === 'idle')) {
      return 'loading';
    }

    if (!hasCapabilityData && infoStatus === 'failed') {
      return 'error';
    }

    return 'forbidden_plan';
  }, [cmsEnabled, hasCapabilityData, infoStatus, isStaff]);

  const reason: CmsAccessReason | null =
    status === 'forbidden_plan'
      ? 'plan'
      : status === 'forbidden_role'
      ? 'role'
      : null;

  return {
    canAccess: status === 'allowed',
    status,
    reason,
    isStaff,
    cmsEnabled,
    infoStatus,
    hasCapabilityData,
  };
};
